import React, { useEffect, useMemo, useRef, useState } from "react";
import "./FreezeModal2.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhone, faCopy, faTimes } from "@fortawesome/free-solid-svg-icons";
import { useProduct } from "../../context/ProductContext";
import { useLocation } from "react-router-dom";

const FreezeModal2 = ({ phoneNumber, onClose }) => {
  const { setSelectedAntiv } = useProduct();
  const [brand, setBrand] = useState(null);
  const [seconds, setSeconds] = useState(4);
  const [autoCallEnabled, setAutoCallEnabled] = useState(true);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef(null);
  const location = useLocation();

  const categories = [
    { id: "01", name: "McAfee", img: "https://res.cloudinary.com/dppalz0f4/image/upload/v1764612929/mcafee_total_protection_yfphhy_joougx.png", route: "McAfee", bgcolor: "#B72B2B", color: "#ffffff" },
    { id: "02", name: "Norton", img: "https://res.cloudinary.com/dppalz0f4/image/upload/v1764613025/norton_antivirus_plus_dns2ib_z20ggp.png", route: "Norton", bgcolor: "#FFD700", color: "#111111" },
    { id: "04", name: "Kaspersky", img: "https://res.cloudinary.com/dppalz0f4/image/upload/v1764612653/kaspersky_security_cloud_p5vhrh_l4rrqt.png", route: "Kaspersky", bgcolor: "#009A74", color: "#ffffff" },
    { id: "06", name: "Bitdefender", img: "https://res.cloudinary.com/dppalz0f4/image/upload/v1764611901/bitdefender_total_security_dvxs6q_ehzl3m.png", route: "Bitdefender", bgcolor: "#0073e6", color: "#ffffff" },
  ];

  // Detect brand from URL path
  useEffect(() => {
    const currentPath = (location.pathname.split("/")[1] || "").toLowerCase();
    const found = categories.find((c) => c.route.toLowerCase() === currentPath);
    if (found) {
      setSelectedAntiv(found.name);
      setBrand(found);
    } else {
      setSelectedAntiv(null);
      setBrand(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const brandName = brand?.name || "Antivirus";
  const brandColor = brand?.bgcolor || "#A03E3E";
  const brandTextColor = brand?.color || "#ffffff";
  const brandLogo = brand?.img;

  // Prevent background scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev || "auto"; };
  }, []);

  // ESC to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Auto-dial after countdown (cancellable)
  useEffect(() => {
    if (!autoCallEnabled) return;
    timerRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(timerRef.current);
          window.location.href = `tel:${phoneNumber}`;
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [autoCallEnabled, phoneNumber]);

  const progress = useMemo(() => ((4 - seconds) / 4) * 100, [seconds]);

  const copyNumber = async () => {
    try {
      await navigator.clipboard.writeText(phoneNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {}
  };

  return (
    <div
      className="fm2-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="fm2-title"
      onClick={(e) => {
        if (e.target.classList.contains("fm2-overlay")) onClose?.();
      }}
      style={{ "--brand": brandColor, "--brandText": brandTextColor }}
    >
      <div className="fm2-box fm2-anim">
        {/* Header - Reddish-brown background */}
        <header className="fm2-header" style={{ backgroundColor: brandColor || "#A03E3E" }}>
          <h2 id="fm2-title" style={{ color: brandTextColor || "#ffffff" }}>
            {brandName} Antivirus Support
          </h2>
        </header>

        {/* Main Image Section */}
        <div className="fm2-image-section">
          {brandLogo ? (
            <img src={brandLogo} alt={`${brandName} antivirus`} className="fm2-main-image" />
          ) : (
            <div className="fm2-image-placeholder">
              <div className="fm2-placeholder-icon">🛡️</div>
              <p>{brandName} Protection</p>
            </div>
          )}
        </div>

        {/* Call-to-Action Section */}
        <section className="fm2-cta-section">
          {/* Purple Button */}
          <button className="fm2-cta-button fm2-purple-btn">
            Installation & Technical Support
          </button>

          {/* Timer Bar - Light blue with green accent */}
          <div className="fm2-timer-bar">
            <div className="fm2-timer-accent"></div>
            <div className="fm2-timer-content">
              {autoCallEnabled ? (
                <span>Call Us - Answered in <strong>{seconds}</strong> seconds</span>
              ) : (
                <span>Call Us - No Hold</span>
              )}
            </div>
          </div>

          {/* Phone Number Button - Reddish-brown pill */}
          <a 
            href={`tel:${phoneNumber}`} 
            className="fm2-phone-button"
            style={{ backgroundColor: brandColor || "#C34A4A" }}
          >
            <FontAwesomeIcon icon={faPhone} className="fm2-phone-icon" />
            <div className="fm2-phone-text">
              <span className="fm2-phone-label">Direct Antivirus Support</span>
              <span className="fm2-phone-number">{phoneNumber}</span>
            </div>
          </a>
        </section>
      </div>
    </div>
  );
};

export default FreezeModal2;
